import { useMemo, useState, useCallback } from 'react';
import { splitSearchWords } from '../utils/wordSearch';

// --- Pure exports (testbar) ---

/**
 * Sortierung mit expliziter Richtung. Vergleich immer aufsteigend definiert,
 * 'desc' invertiert. (Spec §2.1 — die Feld-Defaults setzt die Page.)
 */
export function sortWantlistCards(cards, sortBy = 'name', sortDirection = 'asc') {
  const sorted = [...cards];
  sorted.sort((a, b) => {
    let cmp;
    switch (sortBy) {
      case 'price':
        cmp = (a.price || 0) - (b.price || 0);
        break;
      case 'missing':
        cmp = (a.missing || 0) - (b.missing || 0);
        break;
      case 'name':
      default:
        cmp = (a.name || '').localeCompare(b.name || '', 'de');
    }
    return sortDirection === 'desc' ? -cmp : cmp;
  });
  return sorted;
}

/**
 * Kopfzeilen-Stats (Spec §2.3): fehlen/Wert beschreiben die Sammlung
 * (unabhängig vom Export-Häkchen), exportCount beschreibt die Auswahl.
 * Einheiten: totalCards/totalMissing in Exemplaren, friendsAvailable/
 * exportCount in Karten-Einträgen.
 */
export function computeWantlistStats(enrichedCards) {
  let totalCards = 0;
  let totalMissing = 0;
  let totalValue = 0;
  let friendsAvailable = 0;
  let exportCount = 0;
  for (const c of enrichedCards) {
    totalCards += c.quantity || 0;
    totalMissing += c.missing || 0;
    totalValue += (c.price || 0) * (c.missing || 0);
    if ((c.missing || 0) > 0 && (c.friends?.length || 0) > 0) friendsAvailable += 1;
    if (c.isChecked && (c.missing || 0) > 0) exportCount += 1;
  }
  return { totalCards, totalMissing, totalValue, friendsAvailable, exportCount };
}

// --- Ownership distribution ---

function isBasicLand(card) {
  return (card.superTypes || []).includes('Basic') && (card.types || []).includes('Land');
}

function isExcludedCategory(card, deckCategories) {
  const cats = deckCategories[card.deckId];
  if (!cats || card.categoryId == null) return false;
  const entry = cats.find((c) => c.id === card.categoryId);
  return !!(entry && entry.excluded);
}

function distributeOwnership(cards, deckCategories) {
  // Group by oracleId (basic lands handled separately)
  const byOracle = {};
  for (const c of cards) {
    if (isBasicLand(c)) continue; // basic lands don't participate in oracle distribution
    if (!c.oracleId) continue;
    if (!byOracle[c.oracleId]) byOracle[c.oracleId] = [];
    byOracle[c.oracleId].push(c);
  }

  const result = new Map(); // deckCardId -> { owned, missing, isGrayed, isExcluded, isBasicLand }

  for (const [, group] of Object.entries(byOracle)) {
    let oraclePool = group[0]?.totalOwnedQuantity || 0;

    // Phase 1: printLocked entries first
    const printLocked = group.filter((c) => c.printLocked);
    const notPrintLocked = group.filter((c) => !c.printLocked);

    for (const c of printLocked) {
      const coveredByPrint = Math.min(c.ownedPrintQuantity || 0, c.quantity);
      const missingPrint = c.quantity - coveredByPrint;

      // Consume oracle for covered prints
      oraclePool = Math.max(0, oraclePool - coveredByPrint);

      const isExcluded = isExcludedCategory(c, deckCategories);
      const isFullyCovered = coveredByPrint === c.quantity;

      result.set(c.deckCardId, {
        owned: coveredByPrint,
        missing: missingPrint,
        isGrayed: isFullyCovered || isExcluded,
        isExcluded,
        isBasicLand: false,
      });
    }

    // Phase 2: non-printLocked entries with remaining oracle pool
    for (const c of notPrintLocked) {
      const covered = Math.min(oraclePool, c.quantity);
      const missing = c.quantity - covered;
      oraclePool = Math.max(0, oraclePool - covered);

      const isExcluded = isExcludedCategory(c, deckCategories);
      const isFullyCovered = covered === c.quantity;

      result.set(c.deckCardId, {
        owned: covered,
        missing: missing,
        isGrayed: isFullyCovered || isExcluded,
        isExcluded,
        isBasicLand: false,
      });
    }
  }

  // Handle basic lands (always grayed)
  for (const c of cards) {
    if (isBasicLand(c)) {
      const owned = c.totalOwnedQuantity || 0;
      result.set(c.deckCardId, {
        owned,
        missing: Math.max(0, c.quantity - owned),
        isGrayed: true,
        isExcluded: false,
        isBasicLand: true,
      });
    }
  }

  return result;
}

// --- Main hook ---

export function useWantlistFiltering({
  cards = [],
  deckCategories = {},
  searchTerm = '',
  groupBy = 'category',
  sortBy = 'name',
  sortDirection = 'asc',
  labelFilter = null,
}) {
  // Checkbox state — pro deckCardId ein manueller Override (Session-State)
  const [checkedOverrides, setCheckedOverrides] = useState({});
  // Globale Toggles für Export von excluded und basic-land Karten
  const [exportExcluded, setExportExcluded] = useState(false);
  const [exportBasicLands, setExportBasicLands] = useState(false);

  // Ownership distribution
  const ownership = useMemo(
    () => distributeOwnership(cards, deckCategories),
    [cards, deckCategories],
  );

  // Enrich cards with ownership + grayed + checked state
  const enrichedCards = useMemo(() => {
    return cards.map((c) => {
      const own = ownership.get(c.deckCardId) || {
        owned: 0,
        missing: c.quantity,
        isGrayed: false,
        isExcluded: false,
        isBasicLand: false,
      };

      // Default checkbox: checked if not grayed, unchecked if grayed
      let isChecked = !own.isGrayed;

      // Globale Toggles für excluded / basic-land Kategorien
      if (own.isExcluded && exportExcluded) isChecked = true;
      if (own.isBasicLand && exportBasicLands) isChecked = true;

      // wantlistIncluded override (persisted) — excluded Karten manuell aktivieren
      if (c.wantlistIncluded && own.isExcluded) isChecked = true;

      // Manual override (session state) — gewinnt immer
      if (checkedOverrides[c.deckCardId] !== undefined) {
        isChecked = checkedOverrides[c.deckCardId];
      }

      return {
        ...c,
        owned: own.owned,
        missing: own.missing,
        isGrayed: own.isGrayed,
        isExcluded: own.isExcluded,
        isBasicLand: own.isBasicLand,
        isChecked,
      };
    });
  }, [cards, ownership, checkedOverrides, exportExcluded, exportBasicLands]);

  // Filter
  const filteredCards = useMemo(() => {
    let result = enrichedCards;

    if (searchTerm?.trim()) {
      // Wortweise UND: jedes Wort muss im Kartennamen ODER einem Label vorkommen
      const words = splitSearchWords(searchTerm);
      result = result.filter((c) => {
        const name = (c.name || '').toLowerCase();
        const labelNames = (c.labels || []).map((l) => (l.name || '').toLowerCase());
        return words.every((word) => name.includes(word) || labelNames.some((ln) => ln.includes(word)));
      });
    }

    if (labelFilter) {
      result = result.filter((c) => (c.labels || []).some((l) => l.name === labelFilter));
    }

    return result;
  }, [enrichedCards, searchTerm, labelFilter]);

  // Sort
  const sortedCards = useMemo(
    () => sortWantlistCards(filteredCards, sortBy, sortDirection),
    [filteredCards, sortBy, sortDirection],
  );

  // Group
  const groupedCards = useMemo(() => {
    // Manche Gruppierungen haben eine vorgegebene Reihenfolge — wir seeden
    // das Group-Objekt mit leeren Arrays in der gewünschten Reihenfolge.
    // Dank JS-Insertion-Order der Object-Keys bleibt die Sortierung erhalten.
    const groups = {};
    if (groupBy === 'missing') {
      groups['Fehlt'] = [];
      groups['Vollständig'] = [];
    } else if (groupBy === 'exportstatus') {
      groups['Wird exportiert'] = [];
      groups['Wird nicht exportiert'] = [];
    }

    for (const c of sortedCards) {
      let key;
      switch (groupBy) {
        case 'none':
          key = 'Alle Karten';
          break;
        case 'missing':
          key = c.missing > 0 ? 'Fehlt' : 'Vollständig';
          break;
        case 'category':
          key = c.category || 'Ohne Kategorie';
          break;
        case 'type':
          key = (c.types || []).find((t) => t !== 'Token') || 'Sonstige';
          break;
        case 'set':
          key = c.setCode || 'Unbekannt';
          break;
        case 'friend':
          if (c.friends?.length) {
            for (const f of c.friends) {
              const fKey = f.name;
              if (!groups[fKey]) groups[fKey] = [];
              groups[fKey].push(c);
            }
            continue;
          }
          key = 'Kein Freund';
          break;
        case 'binder':
          if (c.binders?.length) {
            for (const b of c.binders) {
              if (!groups[b.name]) groups[b.name] = [];
              groups[b.name].push(c);
            }
            continue;
          }
          key = 'Nicht in Sammlung';
          break;
        case 'deck':
          key = c.deckName || 'Unbekannt';
          break;
        case 'label':
          if (c.labels?.length) {
            for (const l of c.labels) {
              if (!groups[l.name]) groups[l.name] = [];
              groups[l.name].push(c);
            }
            continue;
          }
          key = 'Ohne Label';
          break;
        case 'exportstatus':
          key = c.isChecked ? 'Wird exportiert' : 'Wird nicht exportiert';
          break;
        default:
          key = 'Alle';
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(c);
    }

    // Leere Seed-Gruppen entfernen (z.B. 'Vollständig' wenn alle fehlen)
    for (const key of Object.keys(groups)) {
      if (groups[key].length === 0) delete groups[key];
    }

    return groups;
  }, [sortedCards, groupBy]);

  // Stats
  const stats = useMemo(() => computeWantlistStats(enrichedCards), [enrichedCards]);

  // Toggle functions
  const toggleCard = useCallback(
    (deckCardId) => {
      setCheckedOverrides((prev) => {
        const card = enrichedCards.find((c) => c.deckCardId === deckCardId);
        const current =
          prev[deckCardId] !== undefined ? prev[deckCardId] : card ? !card.isGrayed : true;
        return { ...prev, [deckCardId]: !current };
      });
    },
    [enrichedCards],
  );

  // Verfügbarkeit der Toggles aus den Daten ableiten — damit die UI sie nur
  // anzeigt, wenn überhaupt Karten in diesen Kategorien existieren.
  const hasExcludedCards = useMemo(
    () => enrichedCards.some((c) => c.isExcluded),
    [enrichedCards],
  );
  const hasBasicLandCards = useMemo(
    () => enrichedCards.some((c) => c.isBasicLand),
    [enrichedCards],
  );

  return {
    enrichedCards,
    filteredCards,
    groupedCards,
    stats,
    toggleCard,
    exportExcluded,
    setExportExcluded,
    exportBasicLands,
    setExportBasicLands,
    hasExcludedCards,
    hasBasicLandCards,
  };
}
